import PropTypes from 'prop-types';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import useClassy from '@core/hooks/useClassy';
import useRdmdOpts from '@core/hooks/useRdmdOpts';
import { useProjectStore } from '@core/store';
import Avatar from '@ui/Avatar';
import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import RDMD from '@ui/RDMD';

import { PermissionContext } from '../../PermissionContext';
import { TutorialProp } from '../Modal/proptypes/tutorials';

import classes from './style.module.scss';

const CardButtonBar = ({
  tutorial,
  makeHero,
  shiftOrder,
  numTutorials,
  openTutorial,
  publish,
  deleteTutorial,
  stagingUrl,
}) => {
  const { _id, published, title, slug } = tutorial;
  const bem = useClassy(classes, 'TutorialCard');

  return stagingUrl ? (
    <button
      className={bem('-Toolbar-Button')}
      onClick={() => {
        window.open(stagingUrl, '_blank');
      }}
    >
      Edit Recipes on Staging
    </button>
  ) : (
    <Fragment>
      <Button
        aria-label="Move card left"
        className={bem('-Toolbar-Arrow')}
        disabled={tutorial.order === 0}
        ghost
        kind="secondary"
        onClick={() => shiftOrder({ _id, direction: -1, slug })}
        size="sm"
      >
        <Icon aria-hidden="true" name="arrow-left" size="md" />
      </Button>
      <Button
        aria-label="Move card right"
        className={bem('-Toolbar-Arrow')}
        disabled={tutorial.order === numTutorials - 1}
        ghost
        kind="secondary"
        onClick={() => shiftOrder({ _id, direction: 1, slug })}
        size="sm"
      >
        <Icon aria-hidden="true" name="arrow-right" size="md" />
      </Button>
      <span className={bem('-Toolbar-Spacer')}>•</span>
      <Button
        className={bem('-Toolbar-Button')}
        ghost
        kind="secondary"
        onClick={() => openTutorial('Edit', tutorial)}
        size="sm"
      >
        Edit
      </Button>
      <Button
        className={bem('-Toolbar-Button')}
        disabled={tutorial.hero}
        ghost
        kind="secondary"
        onClick={() => publish({ ...tutorial, published: !published })}
        size="sm"
        title={tutorial.hero ? 'You can’t unpublish a featured Recipe' : ''}
      >
        {tutorial.published ? 'Unpublish' : 'Publish'}
      </Button>
      <span className={bem('-Toolbar-Spacer')}>•</span>
      {!tutorial.hero && (
        <>
          <Button
            className={bem('-Toolbar-Button')}
            disabled={tutorial.hero}
            ghost
            kind="secondary"
            onClick={() => makeHero({ _id, published, slug })}
            size="sm"
          >
            Feature
          </Button>
          <span className={bem('-Toolbar-Spacer')}>•</span>
        </>
      )}
      <Button
        className={bem('-Toolbar-Button')}
        disabled={!!tutorial.hero && numTutorials > 1}
        ghost
        kind="destructive"
        onClick={() => deleteTutorial({ _id, title, slug })}
        size="sm"
        title={tutorial.hero ? (numTutorials > 1 ? '' : 'You can’t delete a featured Recipe') : ''}
      >
        Delete
      </Button>
    </Fragment>
  );
};

CardButtonBar.propTypes = {
  deleteTutorial: PropTypes.func,
  isAdmin: PropTypes.bool,
  makeHero: PropTypes.func,
  numTutorials: PropTypes.number,
  openTutorial: PropTypes.func,
  publish: PropTypes.func,
  shiftOrder: PropTypes.func,
  stagingUrl: PropTypes.string,
  tutorial: TutorialProp,
};

const renderStatus = (isAdmin, tutorial) => {
  const { emoji, hero, published } = tutorial;
  if (!isAdmin) return false;
  if (hero || !published) {
    return (
      <div
        className={[
          classes['TutorialCard-Status'],
          hero ? classes['TutorialCard-Status_featured'] : '',
          !emoji ? classes['TutorialCard-Status_noemoji'] : '',
        ].join(' ')}
      >
        <i className={[classes['TutorialCard-Status-Icon'], hero ? 'fa fa-star' : 'icon icon-eye-off'].join(' ')} />
        {hero ? 'Featured' : 'Not Published'}
      </div>
    );
  }
  return false;
};

export default function TutorialCard({
  tutorialReview = false,
  className,
  numTutorials,
  tutorial,
  makeHero,
  obstacle = false,
  openTutorial,
  publish,
  shiftOrder,
  deleteTutorial,
  stagingUrl,
}) {
  const bem = useClassy(classes, 'TutorialCard');
  const { t } = useTranslation();

  const isRmdx = useProjectStore(s => s.data.features.mdx === 'enabled');
  const { mdxish, terms, variables } = useRdmdOpts();
  // If project is mdxish enabled, run in safe mode to avoid running user code
  // and evaluating expressions since this component is not in a sandboxed environment
  const rdmdOpts = {
    ...(mdxish && { mdxish, safeMode: true, terms, variables }),
  };

  const tutorialCardClasses = bem(
    className,
    '&',
    obstacle && '_obstacle',
    !tutorial.published && !tutorialReview && '_unpublished',
    'rm-RecipeCard',
  );

  return (
    <li className={tutorialCardClasses} data-testid="recipe-card">
      <div className={bem('-Top')}>
        <PermissionContext.Consumer>
          {isAdmin => renderStatus(isAdmin && !tutorialReview, tutorial)}
        </PermissionContext.Consumer>
        {!!tutorial.emoji && (
          <Avatar className={bem('-Avatar', 'rm-RecipeCard-avatar')} hexColor={tutorial.backgroundColor}>
            {tutorial.emoji}
          </Avatar>
        )}
        <h2 className={bem('-Title')}>{tutorial.title}</h2>
        <div className={bem('rm-Markdown', 'markdown-body', '-Description')}>
          <RDMD opts={rdmdOpts} renderer={isRmdx ? 'rmdx' : 'rdmd'}>
            {tutorial.description}
          </RDMD>
        </div>
        <div className={bem('-Steps')}>{t('recipes.step', { count: parseInt(tutorial.stepCount, 10) })}</div>
        {!obstacle && (
          <Button className={bem('-Button', 'rm-RecipeCard-button')} onClick={() => openTutorial('View', tutorial)}>
            {t('recipes.openRecipe')}
          </Button>
        )}
      </div>
      <PermissionContext.Consumer>
        {isAdmin =>
          !!isAdmin &&
          !tutorialReview &&
          !obstacle && (
            <footer className={bem('-Toolbar')}>
              <Flex align="center" className={bem('-Toolbar-Wrapper')} gap="3px">
                <CardButtonBar
                  deleteTutorial={deleteTutorial}
                  makeHero={makeHero}
                  numTutorials={numTutorials}
                  openTutorial={openTutorial}
                  publish={publish}
                  shiftOrder={shiftOrder}
                  stagingUrl={stagingUrl}
                  tutorial={tutorial}
                />
              </Flex>
            </footer>
          )
        }
      </PermissionContext.Consumer>
    </li>
  );
}

TutorialCard.propTypes = {
  deleteTutorial: PropTypes.func,
  makeHero: PropTypes.func,
  numTutorials: PropTypes.number,
  obstacle: PropTypes.bool,
  openTutorial: PropTypes.func,
  publish: PropTypes.func,
  shiftOrder: PropTypes.func,
  stagingUrl: PropTypes.string,
  tutorial: TutorialProp,
  tutorialReview: PropTypes.bool,
};
